/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';

import { IconButton } from '#UNSAFE_IconButton';
import { BundleType } from '#resources/nls/bundle';
import { StepDirection } from '#hooks/UNSAFE_useSpinning';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';

type Props = {
  /**
   * The children are Icons for the button.
   */
  children: ComponentChildren;

  /**
   * The direction of the step.
   */
  direction: StepDirection;

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean;

  /**
   * Pointer event down handler
   */
  onPointerDown?: (event: PointerEvent) => void;

  /**
   * Pointer event up handler
   */
  onPointerUp?: (event: PointerEvent) => void;

  /**
   * Pointer event out handler
   */
  onPointerOut?: (event: PointerEvent) => void;

  /**
   * Pointer event cancel handler
   */
  onPointerCancel?: (event: PointerEvent) => void;
};

/**
 * A StepButton is used to request a "step up" or "step down" to a value in NumberInputText.
 * @param children The children are Icons for the button
 * @param direction The direction of the step
 * @param isDisabled Whether the button should be disabled
 * @param onPointerDown Handler called when pointer is down
 * @param onPointerUp Handler called when pointer is up
 * @param onPointerOut Handler called when pointer is out
 * @param onPointerCancel Handler called when pointer is canceled
 */
export function StepButton({
  direction,
  isDisabled,
  children,
  onPointerDown,
  onPointerUp,
  onPointerOut,
  onPointerCancel
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const labelIncrease = translations.inputNumber_increase();
  const labelDecrease = translations.inputNumber_decrease();
  const label = direction === 'increase' ? labelIncrease : labelDecrease;

  return (
    <TabbableModeContext.Provider value={{ isTabbable: false }}>
      <div
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerOut={onPointerOut}
        onPointerCancel={onPointerCancel}>
        <IconButton isDisabled={isDisabled} tooltip={label} variant="ghost" size="sm">
          {children}
        </IconButton>
      </div>
    </TabbableModeContext.Provider>
  );
}
